import { useEffect, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import type { Conversation } from "../components/Sidebar";
import MessageBubble from "../components/MessageBubble";
import ChatHeader from "../components/ChatHeader";
import { getValidIdToken, decodeJwt } from "../auth/token";
import "./Chat.css";

interface Message {
  role: "user" | "assistant" | "tool";
  content: string;
}

const API_URL = import.meta.env.VITE_API_URL;

export default function Chat() {
  // ===============================
  // STATE
  // ===============================
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState("");
  const [improveAccuracy, setImproveAccuracy] = useState(false);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // ✏️ EDITING (RESTORED)
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");

  const [token, setToken] = useState<string | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  

  // ===============================
  // AUTH
  // ===============================
  useEffect(() => {
    (async () => {
      try {
        setToken(await getValidIdToken());
      } catch {
        window.location.href = "/login";
      }
    })();
  }, []);

  const decoded = decodeJwt(token);
  const realName =
    decoded?.name ||
    decoded?.preferred_username ||
    decoded?.email ||
    "User";

  // ===============================
  // LOAD CONVERSATIONS (LHS)
  // ===============================
  const loadConversations = async () => {
    const res = await fetch(`${API_URL}/get-all-chat-history`, {
      headers: {
        Authorization: "Bearer " + (await getValidIdToken()),
      },
    });

    if (res.ok) {
      setConversations(await res.json());
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  const ensureConversationId = () => {
    if (conversationId) return conversationId;

    const newId = crypto.randomUUID();
    setConversationId(newId);
    return newId;
  };

  // ===============================
  // LOAD MESSAGES (RHS)
  // ===============================
  const loadConversationMessages = async (cid: string) => {
    const res = await fetch(`${API_URL}/conversations/${cid}/messages`, {
      headers: {
        Authorization: "Bearer " + (await getValidIdToken()),
      },
    });

    if (!res.ok) return;

    const data = await res.json();
    setMessages(
      data.map((m: any) => ({
        role: m.role,
        content: m.content,
      }))
    );
  };


    // -------------------------------
  // Sanitize output
  // -------------------------------
  const sanitize = (text: string) => {
    let t = text
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "    ")
      .replace(/\r/g, "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
    return t.replace(/\n/g, "<br>");
  };

  // ===============================
  // COMPOSE QUERY (HISTORY)
  // ===============================
  const composeQuery = (newUserText: string) => {
    const lines: string[] = [];
    lines.push("You are continuing a conversation. Use the full history below.");
    lines.push("");
    lines.push("--- Conversation so far ---");

    for (const m of messages) {
      const who = m.role === "user" ? "User" : "Assistant";
      lines.push(`${who}: ${m.content}`);
    }

    lines.push("");
    lines.push("--- New user message ---");
    lines.push(newUserText);

    return lines.join("\n");
  };


  const smartAppend = (prev: string, next: string) => {
        if (!prev) return next;
        if (!next) return prev;

        // Preserve newlines exactly
        // if (prev.endsWith("\n") || next.startsWith("\n")) {
        //   return prev + next;
        // }

    return prev + next;
  };




  
  // ===============================
  // STREAM LLM
  // ===============================
  const runLLM = async (userText: string) => {
    const composedQuery = composeQuery(userText);
    const cid = ensureConversationId();

    try {
      const freshToken = await getValidIdToken();

      const res = await fetch(`${API_URL}/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + freshToken,
        },
        body: JSON.stringify({
          query: composedQuery,
          conversation_id: cid,
          improve_accuracy: improveAccuracy,
        }),

      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response stream");

      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let phase: "planning" | "execution" | "answer" | null = null;
      let finalAnswer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const rawLine of lines) {
          console.log("RAW LINE:", rawLine);

          
        // ---------------- SSE ONLY ----------------
          //if (!rawLine.startsWith("data:")) continue;

          // Remove ONLY the prefix
          // const payload = rawLine.slice(5).trimStart();

          // // SSE rule: empty data = newline
          // const text = payload === "" ? "\n" : payload;

          const payload = rawLine.slice(5); // DO NOT trim

          // SSE rule: empty data line = newline
          let text: string;
          if (payload === "") {
            text = "\n";
          } else {
            text = payload.startsWith(" ") ? payload.slice(1) : payload;
          }

          if (payload.trim() === "[DONE]") {
            continue;
          }


          // -------- PLANNING --------
          if (text === "=== PLANNING ===") {
            phase = "planning";
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: "assistant",
                content: "Tool Planning...",
              };
              return copy;
            });
            continue;
          }

          // -------- EXECUTION --------
          if (text === "=== EXECUTION ===") {
            phase = "execution";
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: "assistant",
                content: "Execution Tools...",
              };
              return copy;
            });
            continue;
          }

          // -------- ANSWER --------
          if (text === "=== ANSWER ===") {
            phase = "answer";
            finalAnswer = "";
            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: "assistant",
                content: "Thinking...",
              };
              return copy;
            });
            continue;
          }

          // -------- STREAM TOKENS --------
          if (phase === "answer") {
            // 🔑 Append EXACTLY what the backend sends
            finalAnswer = smartAppend(finalAnswer, text);

            setMessages((prev) => {
              const copy = [...prev];
              copy[copy.length - 1] = {
                role: "assistant",
                content: finalAnswer,
              };
              return copy;
            });
          }
        }
      }


      if (phase === "answer" && buffer) {
        finalAnswer += "\n" + buffer;
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            role: "assistant",
            content: finalAnswer,
          };
          return copy;
        });
      }
    } catch (err: any) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Error: " + err.message,
        };
        return copy;
      });
    }
  };

  // ===============================
  // SEND MESSAGE
  // ===============================
  const sendMessage = async () => {
    if (!question.trim()) return;

    const userText = question.trim();
    setQuestion("");

    setMessages((prev) => [
      ...prev,
      { role: "user", content: userText },
      { role: "assistant", content: "" },
    ]);

    await runLLM(userText);

    // 🔑 refresh LHS immediately
    await loadConversations();
  };

  // ===============================
  // EDIT MESSAGE (RESTORED)
  // ===============================
  const submitEdit = async () => {
    if (editingIndex === null || !editingText.trim()) return;

    const newText = editingText.trim();

    const newMessages = messages.slice(0, editingIndex);
    newMessages.push({ role: "user", content: newText });
    newMessages.push({ role: "assistant", content: "" });

    setMessages(newMessages);
    setEditingIndex(null);
    setEditingText("");

    await runLLM(newText);
  };

  const lastUserIndex = [...messages]
    .map((m) => m.role)
    .lastIndexOf("user");

  // ===============================
  // NEW CHAT
  // ===============================
  const startNewChat = () => {
    setConversationId(null);
    setMessages([{ role: "assistant", content: "Hello! How can I help?" }]);
  };

  // ===============================
  // AUTO SCROLL
  // ===============================
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // ===============================
  // RENDER
  // ===============================
  return (
    <div className="page">
      <Sidebar
        realName={realName}
        conversations={conversations}
        activeConversationId={conversationId}
        onSelectConversation={async (id) => {
          setConversationId(id);
          await loadConversationMessages(id);
        }}
        onNewChat={startNewChat}
      />

      <div className="chat-container">
        <ChatHeader />

        <div className="chat" ref={chatRef}>
          {messages.map((m, i) => (
            <MessageBubble
              key={i}
              role={m.role}
              content={sanitize(m.content)}
              onEdit={
                m.role === "user" && i === lastUserIndex
                  ? () => {
                      setEditingIndex(i);
                      setEditingText(m.content);
                    }
                  : undefined
              }
            />
          ))}
        </div>

        <div className="input-area">
          <textarea
            value={editingIndex !== null ? editingText : question}
            onChange={(e) =>
              editingIndex !== null
                ? setEditingText(e.target.value)
                : setQuestion(e.target.value)
            }
            placeholder={
              editingIndex !== null
                ? "Edit your message…"
                : "Type your message…"
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                editingIndex !== null ? submitEdit() : sendMessage();
              }
            }}
          />

          <label>
            <input
              type="checkbox"
              checked={improveAccuracy}
              onChange={(e) => setImproveAccuracy(e.target.checked)}
            />
            Deep Thinking
          </label>

          {editingIndex !== null ? (
            <>
              <button onClick={submitEdit}>Apply</button>
              <button
                onClick={() => {
                  setEditingIndex(null);
                  setEditingText("");
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button onClick={sendMessage}>Send</button>
          )}
        </div>
      </div>
    </div>
  );
}
